import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/react-hooks";
import { useHistory } from "react-router-dom";
import { Typography, Box, makeStyles } from "@material-ui/core";
import { DatePicker, Day } from "@material-ui/pickers";
import { IntroText, FormActionDrawer, Loader } from "../../../components";
import { useHeader } from "../../../contexts";
import { GET_AVAILABLE_DATES } from "../../../queries";
import { useTranslation } from "react-i18next";
import { Alert } from "@material-ui/lab";

const useClasses = makeStyles(theme => ({
  availableDate: {
    "& button": {
      backgroundColor: theme.palette.grey["100"],
      "&.MuiPickersDay-daySelected": {
        backgroundColor: theme.palette.primary.main
      }
    }
  },
  datePickerContainer: {
    "& .MuiPickersBasePicker-pickerView": {
      margin: "0 auto"
    }
  }
}));

const ScheduleDate = ({
  setDrawerClearance,
  updateScheduleData,
  scheduleData,
  isSubmitting
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const classes = useClasses();
  const { setTitle } = useHeader();

  const [error, setError] = useState(false);
  const [selectedDate, setSelectedDate] = useState();
  const [availableDates, setAvailableDates] = useState([]);
  const [maxDate, setMaxDate] = useState();

  useEffect(() => {
    setTitle(t("schedule.date.title"));
  }, []);

  const { data, loading, error: getDatesError } = useQuery(
    GET_AVAILABLE_DATES,
    {
      variables: {
        groupCode: scheduleData.groupCode
      },
      fetchPolicy: "no-cache",
      onCompleted: ({ availableDates = [] }) => {
        if (!availableDates || availableDates.length < 1) {
          history.push(`/appointment/unavailable`);
        }
      }
    }
  );

  /**
   * Set max date based on last date in array
   */
  useEffect(() => {
    if (availableDates.length > 0)
      setMaxDate(new Date(availableDates[availableDates.length - 1].value));
  }, [availableDates]);

  /**
   * Set available dates
   */
  useEffect(() => {
    if (!data) return;

    const { availableDates = {} } = data;

    if (
      availableDates.available_dates &&
      availableDates.available_dates.length > 0
    ) {
      setAvailableDates(availableDates.available_dates.map(date => date.value));
    }
  }, [data]);

  /**
   * Handle next button
   */
  const handleNext = () => {
    if (selectedDate) {
      updateScheduleData({ date: selectedDate.format("YYYY-MM-DD") });
    } else {
      setError(true);
    }
  };

  /**
   * Returns true or false based on whether date is in available dates array
   *
   * @param {moment} date
   * @param {array} availableDates
   */
  const dateAvailable = (date, availableDates) =>
    availableDates.indexOf(date.format("YYYY-MM-DD")) > -1;

  if (loading) return <Loader />;
  if (getDatesError)
    return (
      <Typography color="error">{t("schedule.date.error_api")}</Typography>
    );

  return (
    <Box data-cy="schedule-screen-date">
      <IntroText>{t("schedule.date.intro_text")}</IntroText>
      {error && (
        <Typography data-cy="schedule-screen-date-error" color="error">
          {t("schedule.date.error")}
        </Typography>
      )}
      {availableDates.length > 0 ? (
        <>
          {selectedDate && (
            <Alert color="success">
              {t("schedule.date.selected_alert_text")}{" "}
              <strong>{selectedDate.format("dddd MMMM Do, YYYY")}</strong>
            </Alert>
          )}
          <Box data-cy="date-picker" className={classes.datePickerContainer}>
            <DatePicker
              orientation="portrait"
              variant="static"
              openTo="date"
              disableToolbar={true}
              value={selectedDate}
              onChange={date => {
                if (dateAvailable(date, availableDates)) setSelectedDate(date);
              }}
              disablePast={true}
              renderDay={(day, selectedDate, isInCurrentMonth, dayComponent) =>
                dateAvailable(day, availableDates) ? (
                  <Box className={classes.availableDate}>{dayComponent}</Box>
                ) : (
                  <Day disabled={true}>{day.format("D")}</Day>
                )
              }
            />
          </Box>
        </>
      ) : (
        <Typography paragraph>{t("schedule.date.error_empty")}</Typography>
      )}
      {selectedDate && (
        <FormActionDrawer
          setDrawerClearance={setDrawerClearance}
          handleNext={() => handleNext()}
          loading={isSubmitting}
        />
      )}
    </Box>
  );
};

export { ScheduleDate };
